import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import { List, Space } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ApprovalType, ApprovalWithAssociatedEntity } from 'api/home';
import { SpaceTabId, TaskId } from 'api/models';
import { ReactComponent as Approved20 } from 'assets/svgs/approved.svg';
import { Avatar } from 'components/atoms/avatar';
import { Collapse, Panel, PanelHeaderTitle } from 'components/atoms/collapse';
import { DueDate } from 'components/atoms/due-date';
import { IconShell } from 'components/atoms/icon-shell';
import { RocketDocument16 } from 'components/atoms/icons';
import { LinkToTask } from 'components/atoms/link-to-task';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { BodyShort01, Text02, Text03 } from 'components/styles';
import { Task } from 'data/tasks';
import { fullName } from 'utils/full-name';
import { getSpaceTabDetailUrl } from 'utils/urls';
import styles from './overview.module.css';

interface ApprovalsProps {
  data: ApprovalWithAssociatedEntity[];
  onTaskApprovalClick: (item: Task) => void;
}
interface ApprovalListProps {
  data: ApprovalWithAssociatedEntity[];
  onItemClick?: (item) => void;
  type: ApprovalType;
}

interface ApprovalItemProps {
  item: ApprovalWithAssociatedEntity;
}

const getFullTaskName = (projectName = '', phaseName = '', taskName = '') => {
  return [projectName, phaseName, taskName].filter(Boolean).join(' > ');
};

const getFullSpaceTabName = (
  projectName = '',
  spaceName = '',
  spaceTabName = '',
) => {
  return [projectName, spaceName, spaceTabName].filter(Boolean).join(' > ');
};

const Description = styled.span`
  ${Text02};
  ${BodyShort01};
`;

const Name = styled.div`
  ${Text03};
  padding-right: var(--spacing-05);
  margin-top: var(--spacing-03);
  display: flex;
  align-items: center;
`;

const Rte = styled(RichTextEditor)`
  --rte-min-height: 16px;
  --rte-background: transparent;
  --rte-readonly-content-padding: 0 20% 0 0;
  .ck.ck-content {
    color: var(--text-02);
    & > p {
      &:before {
        content: '“';
      }
      &:after {
        content: '”';
      }
    }
  }
`;

const ApprovalItem: FC<ApprovalItemProps> = ({ item, children }) => {
  return (
    <List.Item
      className={classNames(styles.approvals_list_item, {
        [styles.approvals_list_item_mod_private]: item.private,
      })}
      actions={[
        item.dueDate ? (
          <DueDate timestamp={moment(item.dueDate)} />
        ) : (
          <Description>—</Description>
        ),
      ]}
    >
      <List.Item.Meta
        avatar={
          <Avatar
            name={fullName(item.requestBy)}
            src={item.requestBy.photoUrl}
            companyLogoUrl={item.requestBy.company.companyLogoUrl}
          />
        }
        title={<strong>{fullName(item.requestBy)}</strong>}
        description={
          <>
            <Rte
              disallowImages
              readonly
              data={
                item.comments?.find(
                  (c) => c.createdBy === item.requestBy.userId,
                )?.content || '—'
              }
            />
            <Description>
              <Name>{children}</Name>
            </Description>
          </>
        }
      />
    </List.Item>
  );
};

export const ApprovalList: FC<ApprovalListProps> = ({ data }) => (
  <List
    split={false}
    className={styles.approvals_list}
    itemLayout="horizontal"
    dataSource={data}
    renderItem={(item: ApprovalWithAssociatedEntity) => {
      switch (item.associatedEntityType) {
        case ApprovalType.TaskApproval:
          return (
            <LinkToTask
              openApprovalTab
              id={TaskId(
                item.associatedEntity.project.projectId,
                item.associatedEntity.taskId,
              )}
            >
              <ApprovalItem item={item}>
                {getFullTaskName(
                  item.associatedEntity.project.projectName,
                  item.associatedEntity.projectPhase?.projectPhaseName,
                  item.associatedEntity.taskName,
                )}
              </ApprovalItem>
            </LinkToTask>
          );
        case ApprovalType.SpaceTabApproval:
          return (
            <Link
              to={{
                pathname: getSpaceTabDetailUrl(
                  SpaceTabId(
                    item.associatedEntity.space.project.projectId,
                    item.associatedEntity.space.spaceId,
                    item.associatedEntity.spaceTabId,
                  ),
                ),
                search: 'tab=approvals',
              }}
            >
              <ApprovalItem item={item}>
                <Space align="center">
                  <RocketDocument16 />
                  {getFullSpaceTabName(
                    item.associatedEntity.space.project.projectName,
                    item.associatedEntity.space.spaceName,
                    item.associatedEntity.spaceTabTitle,
                  )}
                </Space>
              </ApprovalItem>
            </Link>
          );
        default:
          return null;
      }
    }}
  />
);

export const Approvals: FC<ApprovalsProps> = ({
  data,
  onTaskApprovalClick,
}) => {
  const taskApprovals = data.filter(
    (approval) => approval.associatedEntityType === ApprovalType.TaskApproval,
  );

  const docApprovals = data.filter(
    (approval) =>
      approval.associatedEntityType === ApprovalType.SpaceTabApproval,
  );

  return taskApprovals?.length > 0 || docApprovals?.length > 0 ? (
    <Collapse
      key="approvals"
      className={styles.collapse}
      ghost
      defaultActiveKey="approvals"
      expandIconPosition="right"
      expandIcon={({ isActive }) => {
        return isActive ? <CaretUp16 /> : <CaretDown16 />;
      }}
    >
      <Panel
        key="approvals"
        showArrow
        header={
          <PanelHeaderTitle
            icon={<IconShell size="large" icon={<Approved20 />} />}
            title={<FormattedMessage id="approvals" />}
          />
        }
      >
        {taskApprovals?.length > 0 && (
          <ApprovalList
            data={taskApprovals}
            onItemClick={onTaskApprovalClick}
            type={ApprovalType.TaskApproval}
          />
        )}
        {docApprovals?.length > 0 && (
          <ApprovalList
            data={docApprovals}
            type={ApprovalType.SpaceTabApproval}
          />
        )}
      </Panel>
    </Collapse>
  ) : null;
};
